import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import IRMA from "@models/IRMA";
import { useContext } from "react";
import { OwCheckbox } from "@components/shared/OwCheckbox/OwCheckbox";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import { OwPill } from "@components/shared/OwPill/OwPill";
import { Link } from "react-router-dom";
import UserService from "@services/UserService";
import IOwTableCols from "@models/IOwTableCols";
import SortOrderEnum from "@models/SortOrderEnum";
import IAction from "@models/IAction";
import RmaStatusEnum from "@models/RmaStatusEnum";

export interface RmaColsProps {
    toggleConfirmDelete: (row: IRMA, index: number) => void;
    rmas: IRMA[];
    orderBy: string;
    orderDir: SortOrderEnum;
    setOrderBy: (orderBy: string) => void;
    setOrderDir: (orderDir: SortOrderEnum) => void;
    selectedIds: Set<number>;
    onSelectRow: (row: IRMA) => void;
}

export const useRmaCols = ({
    toggleConfirmDelete,
    rmas,
    orderBy,
    orderDir,
    setOrderBy,
    setOrderDir,
    selectedIds,
    onSelectRow,
}: RmaColsProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const getActions = () => {
        const _actions: IAction[] = [];
        // Add bulk actions here if needed in the future
        return _actions;
    };

    const renderActionsFn = (row: IRMA, index: number) => {
        const isChecked = selectedIds.has(row.id);
        const isDraft = row.rma_status_id === RmaStatusEnum.DRAFT;

        return (
            <div className={"d-flex"}>
                {/* <OwCheckbox
                    checked={isChecked}
                    className={"me-4 d-print-none pointer"}
                    label={""}
                    skipTranslate={true}
                    name={""}
                    fn={() => {
                        onSelectRow(row);

                        const elem = document.getElementById(`row-${index}`);
                        if (elem) {
                            if (!isChecked) {
                                elem.classList.add("highlight-row");
                            } else {
                                elem.classList.remove("highlight-row");
                            }
                        }
                    }}
                    truthyVal={true}
                    falsyVal={false}
                /> */}

                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {
                        toggleConfirmDelete(row, index);
                    }}
                    onEditClickFn={() => {}}
                    hideEdit={!isDraft}
                    hideTrash={!isDraft}
                />
            </div>
        );
    };

    const renderRmaNumberFn = (row: IRMA) => {
        return (
            <div>
                <Link to={`/inventory/rma/${row.guid}`}>{row.rma_number}</Link>
            </div>
        );
    };

    const renderStatusFn = (row: IRMA) => {
        let className = "bg-secondary";
        const statusText = row.RMA_Status?.name || "Unknown";

        switch (row.rma_status_id) {
            case RmaStatusEnum.DRAFT:
                className = "bg-secondary";
                break;
            case RmaStatusEnum.AUTHORIZED:
                className = "bg-primary";
                break;
            case RmaStatusEnum.RECEIVED:
                className = "bg-warning";
                break;
            case RmaStatusEnum.PROCESSED:
                className = "bg-success";
                break;
            case RmaStatusEnum.CANCELLED:
                className = "bg-danger";
                break;
        }

        return (
            <div>
                <OwPill title={statusText} className={`px-2 ${className}`} />
            </div>
        );
    };

    const renderOrderFn = (row: IRMA) => {
        if (row.Order) {
            return (
                <div>
                    <Link to={`/orders/${row.Order.guid}/entry`}>{row.Order.name}</Link>
                </div>
            );
        }
        return null;
    };

    const renderCustomerFn = (row: IRMA) => {
        return <div>{row.Vendor?.name || "-"}</div>;
    };

    const renderWarehouseFn = (row: IRMA) => {
        return <div>{row.Warehouse?.name || "-"}</div>;
    };

    const renderItemsCountFn = (row: IRMA) => {
        const count = row.RMA_Line_Items?.length || 0;
        return <div>{count}</div>;
    };

    const renderTrackingNumberFn = (row: IRMA) => {
        return <div>{row.tracking_number || "-"}</div>;
    };

    const renderCreatedAtFn = (row: IRMA) => {
        return <div>{UserService.parseDate(row.created_at)}</div>;
    };

    const renderUpdatedAtFn = (row: IRMA) => {
        return <div>{UserService.parseDate(row.updated_at)}</div>;
    };

    const sortFn = (col: string) => {
        if (orderBy === col) {
            setOrderDir(orderDir === SortOrderEnum.ASC ? SortOrderEnum.DESC : SortOrderEnum.ASC);
        } else {
            setOrderBy(col);
            setOrderDir(SortOrderEnum.ASC);
        }
    };

    const sortOrderFn = (col: string) => {
        return orderBy === col ? orderDir : SortOrderEnum.ASC;
    };

    const getColumns = (): IOwTableCols[] => {
        const _columns: IOwTableCols[] = [
            // {
            //     title: "actions",
            //     valueProp: "actions",
            //     renderFn: renderActionsFn,
            //     renderFnEmitsRow: true,
            //     isActionsCol: true,
            // },
            {
                title: "rma_number",
                valueProp: "rma_number",
                renderFn: renderRmaNumberFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn: () => sortFn("rma_number"),
                sortOrderFn: () => sortOrderFn("rma_number"),
            },
            {
                title: "status",
                valueProp: "rma_status_id",
                renderFn: renderStatusFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn: () => sortFn("rma_status_id"),
                sortOrderFn: () => sortOrderFn("rma_status_id"),
            },
            {
                title: "order",
                valueProp: "Order.name",
                renderFn: renderOrderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "vendor",
                isVendorOverride: true,
                valueProp: "Vendor.name",
                renderFn: renderCustomerFn,
                renderFnEmitsRow: true,
            },
            {
                title: "warehouse",
                valueProp: "Warehouse.name",
                renderFn: renderWarehouseFn,
                renderFnEmitsRow: true,
                notVisible: !supplierModel.has_warehouses,
            },
            {
                title: "items",
                valueProp: "items_count",
                renderFn: renderItemsCountFn,
                renderFnEmitsRow: true,
            },
            {
                title: "tracking",
                valueProp: "tracking_number",
                renderFn: renderTrackingNumberFn,
                renderFnEmitsRow: true,
            },
            {
                title: "created_at",
                valueProp: "created_at",
                renderFn: renderCreatedAtFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn: () => sortFn("created_at"),
                sortOrderFn: () => sortOrderFn("created_at"),
            },
            {
                title: "updated_at",
                valueProp: "updated_at",
                renderFn: renderUpdatedAtFn,
                renderFnEmitsRow: true,
                isSortable: true,
                sortFn: () => sortFn("updated_at"),
                sortOrderFn: () => sortOrderFn("updated_at"),
            },
        ];

        return _columns;
    };

    return {
        getColumns,
        getActions,
    };
};
